import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AuthService, ToastService } from '@common/services';
import { environment } from 'environments/environment';
import { throwError } from 'rxjs';
import { catchError, exhaustMap, take } from 'rxjs/operators';

import { UserService } from './user.service';

/** `code` del 401 de `verifyToken` en la API: el token ya no es válido (vencido o firmado con otro secreto). */
export const INVALID_TOKEN_CODE = 'INVALID_TOKEN';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
    constructor(private userService: UserService, private injector: Injector) {}

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        return this.userService.user$.pipe(
            take(1),
            exhaustMap(user => {
                const timezone: any = Intl.DateTimeFormat().resolvedOptions().timeZone;

                let outgoing = req;
                const localTenantId = (environment as { localDevTenantId?: string }).localDevTenantId;
                const apiIsLocalProxy = environment.API_URL.startsWith('/');

                const unifiedHosts = ((environment as { unifiedLoginHostnames?: string[] }).unifiedLoginHostnames || []).map(h => String(h).trim().toLowerCase());
                const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
                const isUnifiedHost = unifiedHosts.includes(currentHost);

                const hasExplicitTenantHeader = req.headers.has('tenantid');
                if (!environment.production && localTenantId && apiIsLocalProxy && !isUnifiedHost && !hasExplicitTenantHeader && req.url.startsWith(environment.API_URL)) {
                    outgoing = req.clone({
                        setHeaders: {
                            tenantid: String(localTenantId).trim().toLowerCase(),
                        },
                    });
                }

                if (this.isFinaPrintRequest(outgoing.url)) {
                    return next.handle(outgoing);
                }

                if (!user) {
                    return next.handle(outgoing);
                }

                let headers = outgoing.headers.set('timeZone', timezone);
                headers = headers.set('x-access-token', user.token);
                if (user.tenantId) {
                    headers = headers.set('tenantid', String(user.tenantId).trim().toLowerCase());
                }
                const modifiedReq = outgoing.clone({ headers });

                return next.handle(modifiedReq).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 401 && error.error?.code === INVALID_TOKEN_CODE) {
                            this.endInvalidSession(user.token);
                        }
                        return throwError(() => error);
                    }),
                );
            }),
        );
    }

    /** Solo cierra si el token rechazado sigue siendo el de la sesión: varios 401 en paralelo cierran una vez. */
    private endInvalidSession(rejectedToken: string): void {
        if (this.userService.user$.value?.token !== rejectedToken) return;
        // AuthService depende de HttpClient: resolverlo en el constructor del interceptor arma un ciclo de DI.
        this.injector.get(AuthService).logout();
        this.injector.get(ToastService).error('Sesión expirada', 'Por favor, inicia sesión nuevamente.');
    }

    private isFinaPrintRequest(url: string): boolean {
        const finaPrintUrl = (environment as { finaPrintUrl?: string }).finaPrintUrl || 'http://127.0.0.1:8000';

        try {
            return new URL(url, window.location.origin).origin === new URL(finaPrintUrl, window.location.origin).origin;
        } catch {
            return false;
        }
    }
}
